import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import { restartGraphQLSubscriptionsWebSocket } from 'common/graphql/utils';
import { OnboardingUserDocument } from 'features/Onboarding/graphql/queries/onboardingUser.gql';

import { CreateCapitalDepositMandateeDocument } from '../graphql/mutations/createCapitalDepositMandatee.gql';
import { SubmitCapitalDepositMandateeDocument } from '../graphql/mutations/submitCapitalDepositMandatee.gql';
import { UpdateCompanyNameCapitalDepositMandateeDocument } from '../graphql/mutations/updateCompanyNameCapitalDepositMandatee.gql';
import { CapitalDepositMandateeModuleDocument } from '../graphql/queries/capitalDepositMandateeModule.gql';
import { Model } from './model';

export enum Service {
  CreateCapitalDeposit = 'createCapitalDeposit',
  UpdateCompanyName = 'updateCompanyName',
  SendApplication = 'sendApplication',
}

const services: MachineServices<Model, Service> = {
  createCapitalDeposit: async ({ getModuleData }, event) => {
    if (event.type !== 'SUBMIT_COMPANY_NAME') {
      throw new Error(`createCompany called on event ${event.type}`);
    }

    const { companyContext } = getModuleData();
    const { companyProfileId, setCompanyContext } = companyContext;

    const { legalName } = event.data;

    // The mutation will create the capital deposit entity
    // and the company entity if needed
    const mutationResult = await apolloClient.mutate({
      mutation: CreateCapitalDepositMandateeDocument,
      // We want to refetch the onboardingUser query for the updated viewer.companyUsers
      refetchQueries: [{ query: OnboardingUserDocument }],
      variables: {
        input: {
          companyProfileId,
          legalName,
        },
      },
    });

    if (companyProfileId === undefined) {
      // If the company entity has been created, we need to set company context
      setCompanyContext({
        companyProfileId:
          mutationResult.data?.createCapitalDepositAndRelatedEntities
            .capitalDeposit.companyProfileId,
        companyUserId: undefined, // The SecuredRoute component will fetch the companyUser and set it in the companyContext
      });

      // Restart the websocket connection to ensure GraphQL subscriptions will
      // work with the new companyProfileId
      restartGraphQLSubscriptionsWebSocket();
    }
  },
  sendApplication: async ({ getModuleData }) => {
    const companyProfileId =
      getModuleData().data.viewer.company?.companyProfileId;

    if (!companyProfileId) {
      throw new Error('Missing companyProfileId in sendApplication');
    }

    await apolloClient.mutate({
      mutation: SubmitCapitalDepositMandateeDocument,
      // We want to refetch the onboardingUser query for the updated viewer.companyUser.isAwaitingAccountValidation
      refetchQueries: [
        {
          query: CapitalDepositMandateeModuleDocument,
          variables: {
            companyProfileId,
            includeCompany: true,
          },
        },
      ],
      variables: {
        input: {
          companyProfileId,
        },
      },
    });
  },
  updateCompanyName: async ({ getModuleData }, event) => {
    if (event.type !== 'SUBMIT_COMPANY_NAME') {
      throw new Error(`updateCompanyName called on event ${event.type}`);
    }

    const { companyProfileId } = getModuleData().companyContext;

    if (companyProfileId === undefined) {
      throw new Error(`updateCompanyName called with no company`);
    }

    const { legalName } = event.data;

    await apolloClient.mutate({
      mutation: UpdateCompanyNameCapitalDepositMandateeDocument,
      variables: {
        input: {
          companyProfileId,
          legalName,
        },
      },
    });
  },
};

export default services;
